/**
 * متاتگ‌های سئو سمت کلاینت
 * ==========================
 * سایت تک‌صفحه‌ای (SPA) است، پس همه صفحه‌ها یک `index.html` می‌گیرند. اگر
 * عنوان و توضیح صفحه عوض نشود، گوگل همه صفحه‌ها را با یک عنوان می‌بیند و
 * هیچ‌کدام رتبه نمی‌گیرند.
 *
 * گوگل امروز جاوااسکریپت را اجرا و این تگ‌ها را می‌خواند، ولی شبکه‌های
 * اجتماعی (تلگرام، واتساپ، توییتر) این کار را نمی‌کنند. برای همین آدرس
 * canonical و og:image باید **مطلق** باشند تا در پیش‌نمایش لینک درست بیایند.
 *
 * هر تگی که این فایل می‌سازد نشانه‌گذاری می‌شود (`data-seo`) تا هنگام رفتن به
 * صفحه بعد فقط همان‌ها پاک شوند و تگ‌های ثابتِ `index.html` دست‌نخورده بمانند.
 */
import { useEffect } from 'react'

const MARK = 'data-seo'

export type Seo = {
  title?: string
  description?: string
  keywords?: string
  canonical?: string
  image?: string
  imageAlt?: string
  type?: 'website' | 'article'
  noindex?: boolean
  publishedTime?: string | null
  modifiedTime?: string | null
  author?: string
  section?: string
  /** داده ساختاریافته schema.org — آرایه یا یک شیء */
  jsonLd?: any
  /** لینک صفحه بعد/قبل در فهرست‌های صفحه‌بندی‌شده */
  prev?: string
  next?: string

  // ── تنظیمات سطح سایت که از هاب نشریار می‌آیند ─────────────────────────
  /** الگوی عنوان صفحه؛ `%s` جای عنوان می‌نشیند. مثل «%s | مجله ارزینو» */
  titleTemplate?: string
  /** شناسه ایکس/توییتر ناشر برای `twitter:site` */
  twitterSite?: string
  /** کد تأیید مالکیت گوگل سرچ کنسول */
  googleVerification?: string
  /**
   * صفحه ایندکس نشود ولی لینک‌هایش دنبال شود (`noindex, follow`).
   *
   * با `noindex` فرق دارد: آن یکی `nofollow` هم می‌دهد و برای صفحه‌ای است که
   * اصلاً ارزشی ندارد (مثل نتیجه جستجو). این یکی برای وقتی است که ناشر
   * موقتاً کل وبلاگ را از نتایج بیرون می‌برد ولی نمی‌خواهد اعتبار لینک‌های
   * داخلی‌اش را دور بریزد.
   */
  noindexFollow?: boolean
}

const SITE = 'ارزینو'
const DEFAULT_DESC =
  'ارزینو؛ خرید و فروش امن و سریع بیت‌کوین، تتر و بیش از ۲۰ ارز دیجیتال با کمترین کارمزد.'

function absolute(url?: string): string {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  if (typeof window === 'undefined') return url
  return window.location.origin + (url.startsWith('/') ? url : `/${url}`)
}

function clearOwn() {
  document.querySelectorAll(`[${MARK}]`).forEach((el) => el.remove())
}

function meta(attr: 'name' | 'property', key: string, value?: string) {
  if (!value) return
  const el = document.createElement('meta')
  el.setAttribute(attr, key)
  el.setAttribute('content', value)
  el.setAttribute(MARK, '1')
  document.head.appendChild(el)
}

function link(rel: string, href?: string) {
  if (!href) return
  const el = document.createElement('link')
  el.setAttribute('rel', rel)
  el.setAttribute('href', href)
  el.setAttribute(MARK, '1')
  document.head.appendChild(el)
}

function ld(data: any) {
  if (!data) return
  const blocks = Array.isArray(data) ? data : [data]
  blocks.forEach((block) => {
    if (!block) return
    const el = document.createElement('script')
    el.type = 'application/ld+json'
    el.setAttribute(MARK, '1')
    try {
      el.textContent = JSON.stringify(block)
    } catch {
      return
    }
    document.head.appendChild(el)
  })
}

/** اعمال متاتگ‌ها. خارج از React هم قابل صداکردن است. */
export function applySeo(seo: Seo) {
  if (typeof document === 'undefined') return
  clearOwn()

  // الگوی عنوان (اگر ناشر تعریف کرده باشد) جای قالب پیش‌فرض را می‌گیرد؛ چون
  // خودِ الگو معمولاً نام سایت را در خودش دارد، دیگر `| ارزینو` اضافه نمی‌شود.
  const template = String(seo.titleTemplate || '')
  const title = seo.title
    ? template.includes('%s')
      ? template.replace('%s', seo.title)
      : `${seo.title} | ${SITE}`
    : `${SITE} | صرافی ارز دیجیتال`
  document.title = title.length > 75 ? `${seo.title}` : title

  const desc = seo.description || DEFAULT_DESC
  const canonical = absolute(seo.canonical || window.location.pathname)
  const image = absolute(seo.image)

  meta('name', 'description', desc)
  meta('name', 'keywords', seo.keywords)
  if (seo.noindex) {
    meta('name', 'robots', 'noindex, nofollow')
  } else if (seo.noindexFollow) {
    meta('name', 'robots', 'noindex,follow')
  } else {
    // max-image-preview:large یعنی گوگل تصویر بزرگ کنار نتیجه نشان دهد —
    // تفاوت محسوسی در نرخ کلیک دارد.
    meta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1')
  }
  link('canonical', canonical)
  if (seo.prev) link('prev', absolute(seo.prev))
  if (seo.next) link('next', absolute(seo.next))

  // ---- اوپن‌گراف (تلگرام، واتساپ، لینکدین)
  meta('property', 'og:site_name', SITE)
  meta('property', 'og:locale', 'fa_IR')
  meta('property', 'og:type', seo.type || 'website')
  meta('property', 'og:title', seo.title || SITE)
  meta('property', 'og:description', desc)
  meta('property', 'og:url', canonical)
  meta('property', 'og:image', image)
  meta('property', 'og:image:alt', seo.imageAlt || seo.title)
  if (seo.type === 'article') {
    meta('property', 'article:published_time', seo.publishedTime || undefined)
    meta('property', 'article:modified_time', seo.modifiedTime || undefined)
    meta('property', 'article:author', seo.author)
    meta('property', 'article:section', seo.section)
  }

  // ---- توییتر
  meta('name', 'twitter:card', image ? 'summary_large_image' : 'summary')
  meta('name', 'twitter:site', seo.twitterSite)
  meta('name', 'twitter:title', seo.title || SITE)
  meta('name', 'twitter:description', desc)
  meta('name', 'twitter:image', image)

  // کد تأیید گوگل — فقط اگر `bootstrapSeo` یا خودِ index.html آن را نگذاشته
  // باشد. دو تگ تأیید همزمان، سرچ کنسول را گیج می‌کند.
  if (seo.googleVerification &&
      !document.querySelector('meta[name="google-site-verification"]')) {
    meta('name', 'google-site-verification', seo.googleVerification)
  }

  ld(seo.jsonLd)
}

/**
 * هوک صفحه‌ها.
 *
 * `deps` را خودِ صفحه می‌دهد (مثلاً نشانی مقاله) تا هنگام تغییر داده دوباره
 * اعمال شود. وابسته‌کردن به خود شیء `seo` باعث اجرای بی‌پایان می‌شد، چون شیء
 * در هر رندر تازه ساخته می‌شود.
 */
export function useSeo(seo: Seo | null, deps: any[] = []) {
  useEffect(() => {
    if (!seo) return
    applySeo(seo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/**
 * راه‌اندازی یک‌باره سئو در ریشه برنامه.
 *
 * سه چیز را از تنظیمات مدیر می‌گیرد و به `<head>` می‌افزاید:
 * کد تأیید مالکیت گوگل سرچ کنسول، کد تحلیل ترافیک، و داده ساختاریافته سازمان.
 *
 * این‌ها عمداً از پایگاه داده می‌آیند نه از فایل، تا مدیر بدون انتشار دوباره
 * سایت بتواند سایت را در سرچ کنسول تأیید کند.
 */
export async function bootstrapSeo(fetchConfig: () => Promise<any>) {
  if (typeof document === 'undefined') return
  let cfg: any = null
  try {
    cfg = await fetchConfig()
  } catch {
    cfg = null
  }
  const origin = cfg?.siteUrl || window.location.origin

  const mark = 'data-seo-boot'
  if (document.querySelector(`[${mark}]`)) return

  if (cfg?.googleVerification) {
    const el = document.createElement('meta')
    el.setAttribute('name', 'google-site-verification')
    el.setAttribute('content', String(cfg.googleVerification))
    el.setAttribute(mark, '1')
    document.head.appendChild(el)
  }

  const org = document.createElement('script')
  org.type = 'application/ld+json'
  org.setAttribute(mark, '1')
  org.textContent = JSON.stringify(organizationLd(origin))
  document.head.appendChild(org)

  // تحلیل ترافیک — فقط اگر مدیر شناسه‌ای وارد کرده باشد. بدون شناسه هیچ
  // اسکریپت بیرونی بار نمی‌شود؛ نه کندی، نه ردیابی بی‌دلیل کاربر.
  const gid = String(cfg?.analyticsId || '').trim()
  if (gid) {
    const s = document.createElement('script')
    s.async = true
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gid)}`
    s.setAttribute(mark, '1')
    document.head.appendChild(s)
    const inline = document.createElement('script')
    inline.setAttribute(mark, '1')
    inline.textContent =
      `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)};` +
      `gtag('js',new Date());gtag('config',${JSON.stringify(gid)});`
    document.head.appendChild(inline)
  }
}

/** داده ساختاریافته صفحه اصلی و سازمان — یک بار در ریشه سایت اعمال می‌شود. */
export function organizationLd(origin: string) {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE,
      url: origin,
      logo: `${origin}/logo.png`,
      sameAs: [] as string[],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE,
      url: origin,
      inLanguage: 'fa-IR',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${origin}/blog?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  ]
}
